import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { api } from '../../api.js';
import { diffLines, foldDiffContext, type DiffHunk, type DiffLine } from './lineDiff.js';

interface TextDiffProps {
  depotPath: string;
  leftRev: number;
  rightRev: number;
}

// GitHub-ish unified diff. We compute the LCS once, fold long runs
// of unchanged context, and render. Virtualized for files that
// produce a few thousand or more rows.
export function TextDiff({ depotPath, leftRev, rightRev }: TextDiffProps) {
  const leftQ = useQuery({
    queryKey: ['depot.file.rev', depotPath, leftRev],
    queryFn: async () => {
      const res = await fetch(api.fileUrl(depotPath, leftRev), { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status} for #${leftRev}`);
      return res.text();
    },
  });
  const rightQ = useQuery({
    queryKey: ['depot.file.rev', depotPath, rightRev],
    queryFn: async () => {
      const res = await fetch(api.fileUrl(depotPath, rightRev), { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status} for #${rightRev}`);
      return res.text();
    },
  });

  if (leftQ.isLoading || rightQ.isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner />
        <span className="text-sm text-muted-foreground">Fetching both revisions…</span>
      </div>
    );
  }
  if (leftQ.error || rightQ.error) {
    const err = (leftQ.error ?? rightQ.error) as Error;
    return (
      <Alert tone="destructive">
        <AlertDescription>{err.message}</AlertDescription>
      </Alert>
    );
  }
  return (
    <DiffBody
      left={leftQ.data ?? ''}
      right={rightQ.data ?? ''}
      leftRev={leftRev}
      rightRev={rightRev}
    />
  );
}

function DiffBody({
  left,
  right,
  leftRev,
  rightRev,
}: {
  left: string;
  right: string;
  leftRev: number;
  rightRev: number;
}) {
  const hunks = useMemo<DiffHunk[]>(() => {
    const lines = diffLines(left, right);
    return foldDiffContext(lines, 3);
  }, [left, right]);

  const stats = useMemo(() => {
    let adds = 0;
    let dels = 0;
    for (const h of hunks) {
      if (h.folded) continue;
      for (const l of h.lines) {
        if (l.kind === 'add') adds++;
        else if (l.kind === 'remove') dels++;
      }
    }
    return { adds, dels };
  }, [hunks]);

  if (stats.adds === 0 && stats.dels === 0) {
    return (
      <Alert>
        <AlertDescription>
          No textual differences between #{leftRev} and #{rightRev}.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground">
          Comparing #{leftRev} → #{rightRev}
        </span>
        <span className="text-xs text-green-400">+{stats.adds}</span>
        <span className="text-xs text-red-400">−{stats.dels}</span>
      </div>
      <VirtualizedDiff hunks={hunks} />
    </div>
  );
}

const ROW_HEIGHT = 22;
const RENDER_BUFFER = 20;

type FlatItem =
  | { kind: 'line'; line: DiffLine }
  | { kind: 'fold'; hunkIdx: number; lineCount: number };

function flatten(hunks: DiffHunk[], expanded: ReadonlySet<number>): FlatItem[] {
  const out: FlatItem[] = [];
  hunks.forEach((h, idx) => {
    if (h.folded && !expanded.has(idx)) {
      out.push({ kind: 'fold', hunkIdx: idx, lineCount: h.lines.length });
    } else {
      for (const line of h.lines) out.push({ kind: 'line', line });
    }
  });
  return out;
}

function VirtualizedDiff({ hunks }: { hunks: DiffHunk[] }) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(600);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setViewportHeight(el.clientHeight);
    const ro = new ResizeObserver(() => setViewportHeight(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const items = useMemo(() => flatten(hunks, expanded), [hunks, expanded]);
  const totalHeight = items.length * ROW_HEIGHT;
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - RENDER_BUFFER);
  const endIdx = Math.min(
    items.length,
    Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + RENDER_BUFFER,
  );
  const slice = items.slice(startIdx, endIdx);
  const offsetY = startIdx * ROW_HEIGHT;

  return (
    <div
      ref={containerRef}
      onScroll={(e) => setScrollTop((e.currentTarget as HTMLDivElement).scrollTop)}
      className="max-h-[70vh] overflow-auto rounded-md border border-neutral-800 bg-background font-mono text-xs"
      style={{ lineHeight: `${ROW_HEIGHT}px` }}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div style={{ position: 'absolute', top: offsetY, left: 0, right: 0 }}>
          {slice.map((item, i) =>
            item.kind === 'line' ? (
              <DiffRow key={`l-${startIdx + i}`} line={item.line} />
            ) : (
              <FoldRow
                key={`f-${item.hunkIdx}`}
                lineCount={item.lineCount}
                onExpand={() =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    next.add(item.hunkIdx);
                    return next;
                  })
                }
              />
            ),
          )}
        </div>
      </div>
    </div>
  );
}

function FoldRow({
  lineCount,
  onExpand,
}: {
  lineCount: number;
  onExpand: () => void;
}) {
  return (
    <div
      style={{ height: ROW_HEIGHT }}
      className="flex items-center justify-center border-y border-neutral-800 bg-neutral-900"
    >
      <button
        type="button"
        onClick={onExpand}
        className="text-xs text-theia-400 hover:underline"
      >
        Show {lineCount} unchanged line{lineCount === 1 ? '' : 's'}
      </button>
    </div>
  );
}

function DiffRow({ line }: { line: DiffLine }) {
  const bg =
    line.kind === 'add'
      ? 'rgba(46, 160, 67, 0.15)'
      : line.kind === 'remove'
      ? 'rgba(248, 81, 73, 0.15)'
      : 'transparent';
  const marker = line.kind === 'add' ? '+' : line.kind === 'remove' ? '−' : ' ';
  const markerColor =
    line.kind === 'add'
      ? 'text-green-400'
      : line.kind === 'remove'
      ? 'text-red-400'
      : 'text-muted-foreground';
  return (
    <div
      style={{
        background: bg,
        height: ROW_HEIGHT,
        lineHeight: `${ROW_HEIGHT}px`,
      }}
      className="grid grid-cols-[40px_40px_16px_1fr]"
    >
      <GutterNo n={line.leftNo} />
      <GutterNo n={line.rightNo} />
      <div className={`text-center ${markerColor}`}>{marker}</div>
      <div className="overflow-x-hidden whitespace-pre pr-2">
        {line.text || ' '}
      </div>
    </div>
  );
}

function GutterNo({ n }: { n: number | null }) {
  return (
    <div className="select-none border-r border-neutral-800 px-1.5 text-right text-muted-foreground">
      {n ?? ' '}
    </div>
  );
}
